import { ArrowRight, Crown, Trophy, Heart } from "lucide-react";

const COLLECTIONS = [
  {
    key: "unity",
    icon: Crown,
    eyebrow: "Collection 01",
    title: "Church Unity Collection",
    trademark: true,
    desc:
      "For ushers, choirs, youth ministries, women's fellowship, men's fellowship and leadership teams. Coordinated polos that unify your whole congregation.",
    bullets: ["Ushers & welcoming teams", "Choirs & worship teams", "Women's & men's fellowship", "Youth ministry"],
    image:
      "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/umipod3h_Samoan%20church%20choir%20uniform%20polo%20shirt.png",
    alt: "Samoan Church Choir Uniforms — choir singing in matching custom Pacific Heritage Polo Shirts, Church Unity Collection NZ",
  },
  {
    key: "conference",
    icon: Trophy,
    eyebrow: "Collection 02",
    title: "Conference Excellence Collection",
    trademark: true,
    desc:
      "For church conferences, camps, events and volunteers. Polos that make your conference look organised, intentional and unforgettable.",
    bullets: ["Annual conferences & assemblies", "Youth & women's camps", "Volunteer & host teams", "Speaker & MC apparel"],
    image:
      "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/czetrdap_Youth%20ministry%20polo%20shirt%20uniform.png",
    alt: "Pacific Youth Ministry Uniforms — youth volunteers in custom Pasifika polo shirts serving community, Church Conference Polo Shirts NZ",
  },
  {
    key: "reunion",
    icon: Heart,
    eyebrow: "Collection 03",
    title: "Reunion Legacy Collection",
    trademark: true,
    desc:
      "For family reunions, village reunions and community gatherings. Custom designs that carry your aiga or kainga name, village colours and shared history.",
    bullets: ["Family reunions (aiga / kainga)", "Village reunions", "Community gatherings", "Cultural festivals"],
    image:
      "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/yzzktg66_Family%20reunion%20uniform%20polo%20shirt.png",
    alt: "Samoan Family Reunion Polo Shirts — Manu'a family reunion in matching custom Pacific Heritage Uniforms NZ",
  },
];

export default function Collections() {
  return (
    <section id="collections" data-testid="collections-section" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">
              Our Collections
            </p>
            <h2 className="mt-3 font-display uppercase text-4xl md:text-6xl tracking-tight max-w-3xl leading-[0.95]">
              Built for Pacific <br />
              <span className="text-teal-400">churches &amp; communities</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md md:text-right">
            Three signature collections covering every group, every role,
            every event your community needs to dress for with pride.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {COLLECTIONS.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.key}
                data-testid={`collection-${c.key}`}
                className="group relative bg-zinc-900 border border-zinc-800 hover:border-teal-400/50 transition-colors overflow-hidden flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1530] via-[#0a1530]/30 to-transparent" />
                  <div className="absolute top-5 left-5 bg-[#060d24]/85 backdrop-blur px-3 py-1.5 border border-gold/40">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">
                      {c.eyebrow}
                    </span>
                  </div>
                  <div className="absolute bottom-5 right-5 w-12 h-12 bg-teal-400 grid place-items-center">
                    <Icon size={22} className="text-zinc-950" />
                  </div>
                </div>

                <div className="p-7 md:p-8 flex flex-col flex-1">
                  <h3 className="font-display uppercase text-2xl md:text-[1.6rem] tracking-tight leading-tight">
                    {c.title}
                    {c.trademark && <sup className="text-gold text-sm ml-1">™</sup>}
                  </h3>
                  <p className="mt-3 text-zinc-400 leading-relaxed text-sm">{c.desc}</p>

                  <ul className="mt-5 space-y-2 flex-1">
                    {c.bullets.map((b) => (
                      <li key={b} className="text-sm text-zinc-300 flex items-start gap-2">
                        <span className="mt-1.5 w-1 h-1 bg-gold" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#contact"
                    data-testid={`collection-cta-${c.key}`}
                    className="mt-7 inline-flex items-center gap-2 text-teal-400 font-bold text-sm uppercase tracking-wider hover:text-teal-300"
                  >
                    Get a free mockup
                    <ArrowRight size={16} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
